import { ImsPermissions } from "@redotech/account-common/permissions";
import { isRedoEmailAddress } from "@redotech/account-common/redo-domains";
import { OmsFlagKey } from "@redotech/feature-flags-keys-oms/keys";
import { useRequiredContext } from "@redotech/react-util/context";
import BarcodeLabelIcon from "@redotech/redo-icons/bar-chart-01.svg";
import BarcodeLabelIconFilled from "@redotech/redo-icons/bar-chart-01_filled.svg";
import CatalogIcon from "@redotech/redo-icons/catalog.svg";
import CatalogIconFilled from "@redotech/redo-icons/catalog_filled.svg";
import ClipboardCheckIcon from "@redotech/redo-icons/clipboard-check.svg";
import ClipboardCheckIconFilled from "@redotech/redo-icons/clipboard-check_filled.svg";
import DashboardIcon from "@redotech/redo-icons/dashboard.svg";
import DashboardIconFilled from "@redotech/redo-icons/dashboard_filled.svg";
import InventoryAdjustmentsIcon from "@redotech/redo-icons/inventory_adjustments.svg";
import LayersIcon from "@redotech/redo-icons/layers-two-01.svg";
import LayersIconFilled from "@redotech/redo-icons/layers-two-01_filled.svg";
import ForecastingIcon from "@redotech/redo-icons/line-chart-up-02.svg";
import ForecastingIconFilled from "@redotech/redo-icons/line-chart-up-02_filled.svg";
import PackageIcon from "@redotech/redo-icons/package.svg";
import PackageIconFilled from "@redotech/redo-icons/package_filled.svg";
import RefreshIcon from "@redotech/redo-icons/refresh-ccw-03_filled.svg";
import RefreshIconOutline from "@redotech/redo-icons/refresh-cw-02.svg";
import RepeatIcon from "@redotech/redo-icons/repeat-01.svg";
import RepeatIconFilled from "@redotech/redo-icons/repeat-01_filled.svg";
import ShoppingBagIcon from "@redotech/redo-icons/shopping-bag-03.svg";
import ShoppingBagIconFilled from "@redotech/redo-icons/shopping-bag-03_filled.svg";
import SuppliersIcon from "@redotech/redo-icons/suppliers.svg";
import {
  useHasPermission,
  useIsRedoAdmin,
} from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useTeamFeatureFlag } from "@redotech/redo-merchant-app-common/use-team-feature-flag";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo } from "react";
import { useLocation } from "react-router-dom";
import { InventoryManagementSuiteStatusesContext } from "../../../../redo-suite/inventory-cloud";
import { CatalogViews } from "./catalog-products";
import { CycleCountViews } from "./cycle-count-views";
import { IntelligentInventoryItem } from "./intelligent-inventory";
import { InventoryAnalyticsItem } from "./inventory-analytics";
import { InventoryLevelsItem } from "./inventory-levels";
import { PurchaseOrdersItem } from "./purchase-orders";
import { ShipmentsItem } from "./shipments";
import { TransfersItem } from "./transfers";

export const InventoryItem = memo(function InventoryItem() {
  const team = useRequiredContext(TeamContext);
  const user = useRequiredContext(UserContext);

  const { imsEnabled } = useRequiredContext(
    InventoryManagementSuiteStatusesContext,
  );

  const isRedoEmail = isRedoEmailAddress(user?.email);

  const isForecastingEnabled = team.settings.ims?.forecastingEnabled ?? false;
  const isForecastingBillingEnabled =
    team.settings.ims?.forecastingBilling?.enabled ?? false;

  const canAccessInventory = useHasPermission(ImsPermissions.ACCESS_INVENTORY);
  const canAccessTransfers = useHasPermission(ImsPermissions.ACCESS_TRANSFERS);

  const showDashboard = imsEnabled && isRedoEmail;
  const showForecastingOnlyPlans =
    !imsEnabled && (isForecastingEnabled || isForecastingBillingEnabled);
  const showInventoryUpsell =
    !imsEnabled && !isForecastingEnabled && !isForecastingBillingEnabled;

  return (
    <>
      <InventoryDashboardItem hidden={!showDashboard} />
      <InventoryAnalyticsItem />
      <CatalogItem />
      <ForecastingItem />
      <IntelligentInventoryItem />
      <InventoryFolderItem
        canAccessInventory={canAccessInventory}
        hideTransfers={!imsEnabled || !canAccessTransfers}
      />
      {/* Unsubscribed merchants: "Inventory" is an upsell link to onboarding. */}
      {showInventoryUpsell && <InventoryUpsellItem />}
      {/* Forecasting-only merchants (no IMS): show onboarding Plans link. */}
      {showForecastingOnlyPlans && <InventoryPlansItem />}
      <PurchasingItem />
      <RecommerceItem />
    </>
  );
});

const InventoryDashboardItem = memo(function InventoryDashboardItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/dashboard`,
      }}
      FilledIcon={DashboardIconFilled}
      friendlyName="Dashboard"
      hidden={hidden}
      id="dashboard"
      UnfilledIcon={DashboardIcon}
    />
  );
});

const CatalogItem = memo(function CatalogItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const { pathname } = useLocation();

  const base = routes.inventoryManagementBaseUrl;
  const isCatalogEnabled = team.settings.ims?.catalogEnabled ?? false;
  const isCatalogChildSelected =
    pathname.startsWith(`${base}/catalog/`) || pathname === `${base}/catalog`;

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isCatalogChildSelected}
      FilledIcon={CatalogIconFilled}
      friendlyName="Catalog"
      hidden={!isCatalogEnabled}
      id="catalog"
      isFolder
      UnfilledIcon={CatalogIcon}
    >
      <CatalogViews />
      <CatalogConsignmentItem />
      <CatalogVariantsItem />
    </RedoSecondaryNavItem>
  );
});

const CatalogConsignmentItem = memo(function CatalogConsignmentItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/catalog/consignment`,
      }}
      friendlyName="Consignment"
      hidden={!(team._id === "68800d96229f8fc10f4dc1ce")}
      id="catalog-consignment"
    />
  );
});

const CatalogVariantsItem = memo(function CatalogVariantsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/variants`,
      }}
      FilledIcon={PackageIconFilled}
      friendlyName="Variants"
      id="product-bins"
      UnfilledIcon={PackageIcon}
    />
  );
});

const ForecastingItem = memo(function ForecastingItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const { pathname } = useLocation();

  const isForecastingEnabled = team.settings.ims?.forecastingEnabled ?? false;
  const isForecastingBillingEnabled =
    team.settings.ims?.forecastingBilling?.enabled ?? false;
  const canAccessForecasting = useHasPermission(
    ImsPermissions.ACCESS_FORECASTING,
  );
  const { enabled: forecastingMonthlyViewFlagEnabled } = useTeamFeatureFlag(
    OmsFlagKey.FORECASTING_MONTHLY_VIEW,
  );
  const { enabled: forecastingPoFlagEnabled } = useTeamFeatureFlag(
    OmsFlagKey.FORECASTING_PO,
  );

  const showForecastingFolder =
    (isForecastingEnabled || isForecastingBillingEnabled) &&
    canAccessForecasting &&
    (forecastingMonthlyViewFlagEnabled || forecastingPoFlagEnabled);

  const isForecastingChildSelected = pathname.startsWith(
    `${routes.inventoryManagementBaseUrl}/forecasting`,
  );

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isForecastingChildSelected}
      FilledIcon={ForecastingIconFilled}
      friendlyName="Forecasting"
      hidden={!showForecastingFolder}
      id="forecasting"
      isFolder
      UnfilledIcon={ForecastingIcon}
    >
      <ForecastingMonthlyItem hidden={!forecastingMonthlyViewFlagEnabled} />
      <ForecastingRecommendationsItem hidden={!forecastingPoFlagEnabled} />
      <ForecastingRulesItem hidden={!forecastingPoFlagEnabled} />
    </RedoSecondaryNavItem>
  );
});

const ForecastingMonthlyItem = memo(function ForecastingMonthlyItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/forecasting/monthly`,
      }}
      friendlyName="Monthly Forecast"
      hidden={hidden}
      id="forecasting-monthly"
      isNew
    />
  );
});

const ForecastingRecommendationsItem = memo(
  function ForecastingRecommendationsItem({ hidden }: { hidden: boolean }) {
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${routes.inventoryManagementBaseUrl}/forecasting/recommendations`,
        }}
        beta
        friendlyName="Purchasing Recommendations"
        hidden={hidden}
        id="forecasting-recommendations"
      />
    );
  },
);

const ForecastingRulesItem = memo(function ForecastingRulesItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/forecasting/rules`,
      }}
      beta
      friendlyName="Rules"
      hidden={hidden}
      id="forecasting-rules"
    />
  );
});

const InventoryFolderItem = memo(function InventoryFolderItem({
  canAccessInventory,
  hideTransfers,
}: {
  canAccessInventory: boolean;
  hideTransfers: boolean;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const { pathname } = useLocation();

  const { imsEnabled } = useRequiredContext(
    InventoryManagementSuiteStatusesContext,
  );

  const isForecastingEnabled = team.settings.ims?.forecastingEnabled ?? false;
  const isForecastingBillingEnabled =
    team.settings.ims?.forecastingBilling?.enabled ?? false;

  const base = routes.inventoryManagementBaseUrl;
  const isInventoryChildSelected =
    pathname.startsWith(`${base}/bill-of-materials`) ||
    pathname.startsWith(`${base}/intelligent-cycle-counts`) ||
    pathname.startsWith(`${base}/adjustments`) ||
    pathname.startsWith(`${base}/products`) ||
    pathname.startsWith(`${base}/barcode-label-templates`) ||
    pathname.startsWith(`${base}/transfers`);

  const showInventoryFolder =
    imsEnabled || isForecastingEnabled || isForecastingBillingEnabled;
  const showInventoryLevels =
    (imsEnabled || isForecastingEnabled || isForecastingBillingEnabled) &&
    canAccessInventory;

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isInventoryChildSelected}
      FilledIcon={PackageIconFilled}
      friendlyName="Inventory"
      hidden={!showInventoryFolder}
      id="inventory"
      isFolder
      UnfilledIcon={PackageIcon}
    >
      <BarcodeLabelTemplatesItem />
      <BillOfMaterialsItem />
      <CycleCountsItem />
      <InventoryAdjustmentsItem />
      {showInventoryLevels && <InventoryLevelsItem />}
      <TransfersItem hidden={hideTransfers} />
    </RedoSecondaryNavItem>
  );
});

const BarcodeLabelTemplatesItem = memo(function BarcodeLabelTemplatesItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  const canAccessInventory = useHasPermission(ImsPermissions.ACCESS_INVENTORY);
  const isRedoEmployee = useIsRedoAdmin();

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/barcode-label-templates`,
      }}
      FilledIcon={BarcodeLabelIconFilled}
      friendlyName="Barcode label templates"
      hidden={!canAccessInventory || !isRedoEmployee}
      id="barcode-label-templates"
      UnfilledIcon={BarcodeLabelIcon}
    />
  );
});

const BillOfMaterialsItem = memo(function BillOfMaterialsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  const { imsEnabled } = useRequiredContext(
    InventoryManagementSuiteStatusesContext,
  );
  const canAccessBillOfMaterials = useHasPermission(
    ImsPermissions.ACCESS_BILL_OF_MATERIALS,
  );

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/bill-of-materials`,
      }}
      FilledIcon={LayersIconFilled}
      friendlyName="Bill of materials"
      hidden={!imsEnabled || !canAccessBillOfMaterials}
      id="bill-of-materials"
      UnfilledIcon={LayersIcon}
    />
  );
});

const CycleCountsItem = memo(function CycleCountsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { pathname } = useLocation();

  const { imsEnabled } = useRequiredContext(
    InventoryManagementSuiteStatusesContext,
  );
  const canAccessCycleCounts = useHasPermission(
    ImsPermissions.ACCESS_CYCLE_COUNTS,
  );

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={pathname.startsWith(
        `${routes.inventoryManagementBaseUrl}/intelligent-cycle-counts`,
      )}
      FilledIcon={ClipboardCheckIconFilled}
      friendlyName="Cycle counts"
      hidden={!imsEnabled || !canAccessCycleCounts}
      id="intelligent-cycle-counts"
      isFolder
      isNew
      UnfilledIcon={ClipboardCheckIcon}
    >
      <CycleCountViews />
      <CycleCountRulesItem />
    </RedoSecondaryNavItem>
  );
});

const CycleCountRulesItem = memo(function CycleCountRulesItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  const canManageCycleCountRules = useHasPermission(
    ImsPermissions.MANAGE_CYCLE_COUNT_RULES,
  );

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/intelligent-cycle-counts/rules`,
      }}
      FilledIcon={RepeatIconFilled}
      friendlyName="Cycle count rules"
      hidden={!canManageCycleCountRules}
      id="cycle-count-rules"
      UnfilledIcon={RepeatIcon}
    />
  );
});

const InventoryAdjustmentsItem = memo(function InventoryAdjustmentsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  const { imsEnabled } = useRequiredContext(
    InventoryManagementSuiteStatusesContext,
  );
  const canAccessInventory = useHasPermission(ImsPermissions.ACCESS_INVENTORY);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/adjustments`,
      }}
      FilledIcon={InventoryAdjustmentsIcon}
      friendlyName="Inventory adjustments"
      hidden={!imsEnabled || !canAccessInventory}
      id="inventory-adjustments"
      UnfilledIcon={InventoryAdjustmentsIcon}
    />
  );
});

/** Upsell link shown to merchants without any inventory subscription. */
const InventoryUpsellItem = memo(function InventoryUpsellItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/onboarding`,
      }}
      FilledIcon={PackageIconFilled}
      friendlyName="Inventory"
      id="inventory-upsell"
      UnfilledIcon={PackageIcon}
    />
  );
});

/** Onboarding link shown to forecasting-only merchants (no IMS). */
const InventoryPlansItem = memo(function InventoryPlansItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/onboarding`,
      }}
      FilledIcon={PackageIconFilled}
      friendlyName="Plans"
      id="inventory-plans"
      UnfilledIcon={PackageIcon}
    />
  );
});

const PurchasingItem = memo(function PurchasingItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { pathname } = useLocation();

  const { imsEnabled } = useRequiredContext(
    InventoryManagementSuiteStatusesContext,
  );
  const canAccessPurchaseOrders = useHasPermission(
    ImsPermissions.ACCESS_PURCHASE_ORDERS,
  );
  const canAccessShipments = useHasPermission(ImsPermissions.ACCESS_SHIPMENTS);

  const base = routes.inventoryManagementBaseUrl;
  const isPurchasingChildSelected =
    pathname.startsWith(`${base}/purchase-orders`) ||
    pathname.startsWith(`${base}/shipments`) ||
    pathname.startsWith(`${base}/suppliers`) ||
    pathname.startsWith(`${base}/document-templates`);

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isPurchasingChildSelected}
      FilledIcon={ShoppingBagIconFilled}
      friendlyName="Purchasing"
      hidden={!imsEnabled}
      id="purchasing"
      isFolder
      UnfilledIcon={ShoppingBagIcon}
    >
      {canAccessPurchaseOrders && <PurchaseOrdersItem />}
      {canAccessShipments && <ShipmentsItem />}
      <SuppliersItem />
    </RedoSecondaryNavItem>
  );
});

const SuppliersItem = memo(function SuppliersItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { pathname } = useLocation();

  const canAccessSuppliers = useHasPermission(ImsPermissions.ACCESS_SUPPLIERS);

  const base = routes.inventoryManagementBaseUrl;
  const isSuppliersChildSelected =
    pathname.startsWith(`${base}/suppliers`) ||
    pathname.startsWith(`${base}/document-templates`);

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isSuppliersChildSelected}
      FilledIcon={SuppliersIcon}
      friendlyName="Suppliers"
      hidden={!canAccessSuppliers}
      id="suppliers"
      isFolder
      UnfilledIcon={SuppliersIcon}
    >
      <AllSuppliersItem />
      <PoTemplatesItem />
    </RedoSecondaryNavItem>
  );
});

const AllSuppliersItem = memo(function AllSuppliersItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/suppliers`,
      }}
      friendlyName="All suppliers"
      id="suppliers-all"
    />
  );
});

const PoTemplatesItem = memo(function PoTemplatesItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  const canAccessPurchaseOrders = useHasPermission(
    ImsPermissions.ACCESS_PURCHASE_ORDERS,
  );

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/document-templates`,
      }}
      friendlyName="PO templates"
      hidden={!canAccessPurchaseOrders}
      id="document-templates"
    />
  );
});

const RecommerceItem = memo(function RecommerceItem() {
  const team = useRequiredContext(TeamContext);
  const { pathname } = useLocation();

  const isMarketplaceEnabled = team.settings.recommerce?.marketplace ?? false;
  const isRecommerceChildSelected =
    pathname.includes("/liquidation/") || pathname.endsWith("/liquidation");

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isRecommerceChildSelected}
      FilledIcon={RefreshIcon}
      friendlyName="Recommerce"
      hidden={!isMarketplaceEnabled}
      id="recommerce"
      isFolder
      UnfilledIcon={RefreshIconOutline}
    >
      <LiquidationDashboardItem />
      <LiquidationAllListingsItem />
    </RedoSecondaryNavItem>
  );
});

const LiquidationDashboardItem = memo(function LiquidationDashboardItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/liquidation/dashboard`,
      }}
      friendlyName="Dashboard"
      id="liquidation-dashboard"
    />
  );
});

const LiquidationAllListingsItem = memo(function LiquidationAllListingsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/liquidation/products`,
      }}
      friendlyName="All listings"
      id="liquidation-all-products"
    />
  );
});
